import {
  ErrorContent,
  Flex,
  HitCounter,
  Table,
  TablePageSizeSelector,
  Text,
} from '@/components';
import { useProjectNameColumn } from '@/features/project/_components/ProjectNameColumn';
import { datetimeFormat } from '@/utils';
import { NEST_ITEM_CONNECTOR } from '@/utils/constants';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../../_hooks/project';
import { NavigateDetailsWithOpenButton } from '../../projects/_components/NavigateDetailsWithOpenButton';
import { useInspectionSearchQueryStates } from '../_hooks/useInspectionSearchQueryStates';

export const InspectionResultTable: FC = () => {
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useInspectionSearchQueryStates();

  const inspectionResults = useProjects({
    params: {
      ...searchQueryStates,
      page: searchQueryStates.page.toString(),
      pageSize: searchQueryStates.pageSize.toString(),
      // 公開後に修正された可能性がある事業を紐づけるフラグ。他のチェック項目が増えた際には動的に渡すようにする
      potentiallyUnpublished: true,
      accountingType: null,
      accountingTypeIds: null,
      corporateNumber: null,
      categories: null,
      endYear: null,
      fundSheetType: searchQueryStates.fundSheetType ?? null,
      implementationMethods: null,
      inspectionByExternalExpertReviewType: null,
      isPublished: null,
      lastImplementedFiscalYearRangeMax: null,
      lastImplementedFiscalYearRangeMin: null,
      isCandidateOfInspectionByExternalExpert: null,
      startYear: null,
      projectNumbers: null,
      skipAdministrativeReformTeamEvaluation: null,
      q: null,
      textSearchCriteria: null,
      ordering: null,
      organizationIds: null,
      policyIds: null,
      otherResponsibleOrganizationIds: null,
      expenditureBudgetPurposeIds: null,
      formulaConditions: null,
      indicatorKeywordSearchOnlyTitleAndDescription: null,
      indicatorType: null,
      indicatorTerm: null,
      indicatorKeyword: null,
      indicatorDataNatureType: null,
      hasOnlyQualitativeIndicator: null,
    },
    enabled: !!(searchQueryStates.sheetType && searchQueryStates.fiscalYear),
  });

  const projectNameColumn = useProjectNameColumn({
    title: t('project-name'),
    dataCount: inspectionResults.data?.results.length ?? 0,
  });

  if (inspectionResults.isError) {
    return <ErrorContent reloadable={false} />;
  }

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center">
        <Flex gap="middle" align="center">
          <HitCounter
            count={inspectionResults.data?.count ?? 0}
            label={t('relevant')}
          />
        </Flex>
        <TablePageSizeSelector
          defaultPageSize={searchQueryStates.pageSize}
          onChange={(newPageSize) => {
            setSearchQueryStates((prev) => ({
              ...prev,
              pageSize: newPageSize,
              page: 1,
            }));
          }}
        />
      </Flex>
      <Table
        dataSource={inspectionResults.data?.results}
        pagination={{
          showSizeChanger: false,
          total: inspectionResults.data?.count,
          current: searchQueryStates.page,
          pageSize: searchQueryStates.pageSize,
        }}
        onChange={(pagination) => {
          if (pagination.current) {
            setSearchQueryStates((v) => ({
              ...v,
              page: pagination.current,
            }));
          }
        }}
        // 事業一覧と共通項目に関しては同じUIを使う
        columns={[
          projectNameColumn,
          {
            title: t('organization-in-charge'),
            key: 'organization',
            width: 500,
            render: (_, record) => {
              return record.organization.flatten.names.join(
                NEST_ITEM_CONNECTOR,
              );
            },
          },
          {
            title: t('last-published-at'),
            key: 'publishedAt',
            width: 160,
            render: (_, record) => (
              <Text
                weight="regular"
                text={
                  record.publishedAt
                    ? datetimeFormat({
                        datetime: record.publishedAt,
                        format: 'YYYY/MM/DD HH:mm',
                      })
                    : undefined
                }
              />
            ),
          },
          {
            title: t('update-lock-datetime'),
            key: 'lockedAt',
            width: 160,
            render: (_, { lockedAt }) => (
              <Text
                weight="regular"
                text={
                  lockedAt
                    ? datetimeFormat({
                        datetime: lockedAt,
                        format: 'YYYY/MM/DD HH:mm',
                      })
                    : undefined
                }
              />
            ),
          },
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => (
              <NavigateDetailsWithOpenButton projectId={record.id} />
            ),
          },
        ]}
      />
    </Flex>
  );
};
